<template>
  <table class="w-full table-fixed">
    <thead>
      <tr class="border-b-2 border-gray-200">
        <th v-if="isSelectable" class="text-left w-2"></th>
        <th v-if="showIcon" class="w-14"></th>
        <th v-if="showName" class="h-14 text-left">
          <button id="name" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.Name") }}
            <span v-if="orderByProperty === 'name'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showType" class="w-30 text-left cursor-default hidden md:table-cell">
          <button id="kind" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.Kind") }}
            <span v-if="orderByProperty === 'kind'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showSize" class="w-25 xl:w-36 pr-6 text-right cursor-default hidden md:table-cell">
          <button id="size" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.Size") }}
            <span v-if="orderByProperty === 'size'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showCreatedDate" class="w-25 xl:w-40 text-right cursor-default hidden sm:table-cell">
          <button id="createddate" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.CreatedDate") }}
            <span v-if="orderByProperty === 'createddate'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showModifiedDate" class="w-25 xl:w-40 text-right cursor-default hidden sm:table-cell">
          <button id="modifieddate" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.ModifiedDate") }}
            <span v-if="orderByProperty === 'modifieddate'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showModifiedBy" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          <button id="modifiedby" class="flex items-center font-semibold focus:outline-none" @click="sortDrive">
            {{ $t("Documents.Table.ModifiedBy") }}
            <span v-if="orderByProperty === 'modifiedby'" class="ml-2" :css-arrow="orderByDirection"></span>
          </button>
        </th>
        <th v-if="showSharedWith" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          {{ $t("Documents.Table.SharedWith") }}
        </th>
        <th v-if="showSharedBy" class="w-1/4 text-left cursor-default pl-4 hidden md:table-cell">
          {{ $t("Documents.Table.SharedBy") }}
        </th>
        <th v-if="showPath" class="text-left">{{ $t("Documents.Table.Location") }}</th>
      </tr>
    </thead>

    <tbody v-if="driveItems && isSelectedDriveItemsReady" class="relative">
      <tr
        v-for="(item, index) in sortedDriveItems"
        :key="item.id"
        v-hover
        class="border-b border-gray-200 hover:bg-gray-150"
        :class="{ 'text-gray-400': item.nctMarkup && item.isUnderStated }"
        style="user-select: none"
        :data-name="item.name"
        :data-id="item.id"
        :data-selected="item.selected"
        @click.exact="select(item, index)"
        @click.ctrl.stop.prevent="select(item, index, 'ctrl')"
        @click.shift.stop.prevent="select(item, index, 'shift')"
        @hover="onDriveItemHover(item)"
      >
        <td v-if="isSelectable" class="relative">
          <div class="flex items-center h-full absolute top-0 pr-2" js-table-checkbox-wrapper>
            <span
              v-if="isClickable"
              class="flex items-center justify-center w-6 h-6 rounded-full bg-white border border-solid border-gray-200"
              js-table-checkbox
            >
              <span class="w-3 h-3 bg-primary-core rounded-full opacity-0"></span>
            </span>
          </div>
        </td>
        <td v-if="showIcon" class="py-2 w-14">
          <DocumentAvatar
            :extension-object="item.extension"
            class="w-10 h-10 mr-4"
            :size-class="'text-2xl'"
          ></DocumentAvatar>
        </td>
        <td v-if="showName">
          <div class="font-semibold opacity-70 inline-block" :title="item.name">
            <span v-if="!item.extension || item.extension.name !== 'Folder'">
              <span v-if="!isClickable">
                {{ item.name }}
              </span>
              <a
                v-else-if="item.webUrl"
                :href="item.webUrl"
                target="_blank"
                class="hover:underline"
                @click="$event.stopPropagation()"
              >
                {{ item.name }}
              </a>
              <a v-else class="hover:underline cursor-pointer" @click.stop="onDriveItemClick(item)">
                {{ item.name }}
              </a>
            </span>

            <a v-else class="hover:underline cursor-pointer" @click.stop.prevent="navigateToFolder(item)">
              {{ item.name }}
            </a>
          </div>
          <br class="sm:hidden" />
          <div v-if="showCreatedDate" class="sm:hidden text-10 inline-block mr-4">
            {{ $d(item.createdDateTime, "date-long") }}
          </div>
          <div v-if="showModifiedDate" class="sm:hidden text-10 inline-block mr-4">
            {{ $d(item.showModifiedDate, "date-long") }}
          </div>
          <div v-if="showSize" class="sm:hidden text-10 inline-block mr-4">
            {{ item.sizeAsString }}
          </div>

          <slot name="document-table-name" :item="item"></slot>
        </td>
        <td v-if="showType" class="cursor-default opacity-80 hidden md:table-cell">
          <span v-if="item.extension">{{ item.extension.name }}</span>
        </td>
        <td v-if="showSize" class="cursor-default pr-6 xl:pr-14 opacity-80 hidden md:table-cell">
          <span>{{ item.sizeAsString }}</span>
        </td>
        <td v-if="showCreatedDate" class="cursor-default opacity-80 hidden sm:table-cell">
          <span class="hidden xl:inline-block">{{ $d(item.createdDateTime, "date-long") }}</span>
          <span class="xl:hidden">{{ $d(item.createdDateTime, "date-short") }}</span>
        </td>
        <td v-if="showModifiedDate" class="cursor-default opacity-80 hidden sm:table-cell">
          <span class="hidden xl:inline-block">{{ $d(item.showModifiedDate, "date-long") }}</span>
          <span class="xl:hidden">{{ $d(item.showModifiedDate, "date-short") }}</span>
        </td>
        <td v-if="showModifiedBy" class="cursor-default pl-4 hidden md:table-cell">
          {{ item && item.lastModifiedBy && item.lastModifiedBy.user ? item.lastModifiedBy.user.displayName : "" }}
        </td>
        <td v-if="showSharedWith || showSharedBy" class="cursor-default pl-4 hidden md:table-cell">
          <slot name="document-table-shared" :item="item"></slot>
        </td>
        <td v-if="showPath">
          <slot name="document-table-path" :item="item"></slot>
        </td>
      </tr>
    </tbody>
    <DocumentTableGhosts
      v-else
      :options="{
        data: driveItems
      }"
    />
  </table>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import DocumentTableGhosts from "@/components/Documents/Table/DocumentTableGhosts.vue";
import DocumentAvatar from "@/components/Documents/DocumentAvatar.vue";
import { sortDriveItems } from "@/helpers/driveItemHelper";
import Hover from "@/directives/Hover";
import Document from "@/models/Documents/Document";

defineOptions({
  name: "DocumentTable",
  directives: { Hover }
});

interface Props {
  driveItems?: any[];
  selectedItems?: any[];
  showIcon?: boolean;
  showName?: boolean;
  showType?: boolean;
  showSize?: boolean;
  showModifiedDate?: boolean;
  showCreatedDate?: boolean;
  showModifiedBy?: boolean;
  showSharedWith?: boolean;
  showSharedBy?: boolean;
  showPath?: boolean;
  isClickable?: boolean;
  isSelectable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  driveItems: () => [],
  selectedItems: () => [],
  showIcon: false,
  showName: false,
  showType: false,
  showSize: false,
  showModifiedDate: false,
  showCreatedDate: false,
  showModifiedBy: false,
  showSharedWith: false,
  showSharedBy: false,
  showPath: false,
  isClickable: true,
  isSelectable: true
});

const emit = defineEmits<{
  (e: "selectedItemsChanged", items: any[]): void;
  (e: "folderClicked", payload: { driveId: string; itemId: string }): void;
  (e: "driveItemHover", item: any): void;
  (e: "driveItemClick", item: any): void;
}>();

const orderByProperty = ref("name");
const orderByDirection = ref("asc");
const isSelectedDriveItemsReady = ref(true);
const lastSelectedIndex = ref(0);

const sortedDriveItems = computed(() => {
  const sortedList = props.driveItems.map(i => new Document(i));

  sortedList.forEach(dc => {
    if (props.selectedItems.some(sd => sd.id === dc.id)) {
      dc.selected = true;
    }
  });

  return sortDriveItems(sortedList, orderByProperty.value, orderByDirection.value);
});

const select = (document, index, modifier?: string) => {
  if (!props.isSelectable) return;

  let selectedDriveItems = [...props.selectedItems];
  if (import.meta.env.VITE_BUILD_TARGET === "nordicworkplace") {
    if (document.selected) {
      selectedDriveItems.splice(selectedDriveItems.indexOf(document), 1);
    } else {
      selectedDriveItems.push(document);
    }
  } else {
    if (document.selected) {
      if (selectedDriveItems.length > 1) {
        if (modifier === "ctrl") {
          selectedDriveItems.splice(selectedDriveItems.indexOf(document), 1);
        } else {
          selectedDriveItems = [];
          selectedDriveItems.push(document);
        }
      } else {
        selectedDriveItems = [];
      }
    } else {
      if (modifier !== "ctrl" && modifier !== "shift") {
        selectedDriveItems = [];
      }

      if (modifier === "shift" && selectedDriveItems.length > 0) {
        if (index > lastSelectedIndex.value) {
          selectedDriveItems.push(...sortedDriveItems.value.slice(lastSelectedIndex.value + 1, index));
        } else {
          selectedDriveItems.push(...sortedDriveItems.value.slice(index + 1, lastSelectedIndex.value));
        }
      }
      selectedDriveItems.push(document);
    }
  }

  lastSelectedIndex.value = index;
  emit("selectedItemsChanged", selectedDriveItems);
};

const navigateToFolder = folder => {
  if (folder.remoteItem) {
    emit("folderClicked", {
      driveId: folder.remoteItem.parentReference.driveId,
      itemId: folder.remoteItem.id
    });
  } else {
    emit("folderClicked", { driveId: folder.parentReference.driveId, itemId: folder.id });
  }
};

const sortDrive = evnt => {
  if (orderByProperty.value === evnt.target.id) {
    orderByDirection.value = orderByDirection.value === "asc" ? "desc" : "asc";
  } else {
    orderByProperty.value = evnt.target.id;
  }
  lastSelectedIndex.value = 0;
};

const onDriveItemHover = driveItem => {
  emit("driveItemHover", driveItem);
};

const onDriveItemClick = driveItem => {
  emit("driveItemClick", driveItem);
};

watch(
  () => props.driveItems,
  async val => {
    isSelectedDriveItemsReady.value = false;
    if (val) {
      const selectedIds = props.selectedItems.map(itm => itm.id);
      const revisedSelectedItems = val.filter(itm => selectedIds.indexOf(itm.id) > -1);
      emit("selectedItemsChanged", revisedSelectedItems);
    }
    isSelectedDriveItemsReady.value = true;
  }
);
</script>

<style scoped>
[css-arrow] {
  width: 0;
  height: 0;
  position: relative;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid transparent;
  transition: transform 0.3s ease-in-out;
}

[css-arrow="asc"] {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid rgba(0, 0, 0, 0.6);
}

[css-arrow="desc"] {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 5px solid rgba(0, 0, 0, 0.6);
  transform: rotate(180deg);
}

[css-arrow="none"] {
  top: 0.0625rem;
  border-top: 0px solid rgba(0, 0, 0, 0.6);
  border-right: 5px solid rgba(0, 0, 0, 0.6);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  border-left: 5px solid rgba(0, 0, 0, 0.6);
}
</style>
